/**
 * ConfettiEffect - Particle-based celebration effect
 *
 * Features:
 * - Configurable particle count and colors
 * - Physics-based falling animation
 * - Multiple particle shapes (squares, circles, rectangles)
 * - Auto-cleanup after animation
 */

import React, { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Generate random number in range
const random = (min, max) => Math.random() * (max - min) + min;

// Generate random integer in range
const randomInt = (min, max) => Math.floor(random(min, max));

// Particle component
const Particle = ({ color, size, startX, startY, shape, delay }) => {
  // Random physics values
  const xVelocity = random(-300, 300);
  const yVelocity = random(-600, -200);
  const rotation = random(0, 720);
  const duration = random(2, 4);

  const shapeStyles = {
    square: { borderRadius: '2px' },
    circle: { borderRadius: '50%' },
    rectangle: { borderRadius: '1px', width: size * 0.4, height: size },
  };

  return (
    <motion.div
      className="absolute pointer-events-none"
      style={{
        left: startX,
        top: startY,
        width: shape === 'rectangle' ? size * 0.4 : size,
        height: size,
        backgroundColor: color,
        ...shapeStyles[shape],
      }}
      initial={{
        opacity: 1,
        x: 0,
        y: 0,
        rotate: 0,
        scale: 1,
      }}
      animate={{
        opacity: [1, 1, 0],
        x: xVelocity,
        y: [0, yVelocity, window.innerHeight],
        rotate: rotation,
        scale: [1, 1.2, 0.8],
      }}
      transition={{
        duration: duration,
        delay: delay,
        ease: [0.25, 0.46, 0.45, 0.94],
        opacity: { times: [0, 0.7, 1] },
        y: { type: 'spring', damping: 10, stiffness: 50 },
      }}
    />
  );
};

const ConfettiEffect = ({
  isActive,
  particleCount = 50,
  colors = ['#f39c12', '#e74c3c', '#3498db', '#2ecc71', '#9b59b6', '#1abc9c'],
  originX = '50%',
  originY = '50%',
  spread = 200,
  duration = 3000,
  onComplete,
}) => {
  const [particles, setParticles] = useState([]);

  // Generate particles when effect becomes active
  useEffect(() => {
    if (isActive) {
      const newParticles = Array.from({ length: particleCount }, (_, i) => ({
        id: `particle-${Date.now()}-${i}`,
        color: colors[randomInt(0, colors.length)],
        size: random(6, 14),
        startX: typeof originX === 'string' ? `calc(${originX} + ${random(-spread/2, spread/2)}px)` : originX + random(-spread/2, spread/2),
        startY: typeof originY === 'string' ? originY : originY,
        shape: ['square', 'circle', 'rectangle'][randomInt(0, 3)],
        delay: random(0, 0.3),
      }));

      setParticles(newParticles);

      // Cleanup after animation
      const timer = setTimeout(() => {
        setParticles([]);
        onComplete?.();
      }, duration);

      return () => clearTimeout(timer);
    } else {
      setParticles([]);
    }
  }, [isActive, particleCount, colors, originX, originY, spread, duration, onComplete]);

  return (
    <AnimatePresence>
      {particles.length > 0 && (
        <div className="fixed inset-0 pointer-events-none z-[9999] overflow-hidden">
          {particles.map((particle) => (
            <Particle key={particle.id} {...particle} />
          ))}
        </div>
      )}
    </AnimatePresence>
  );
};

// Sparkle effect for achievements
export const SparkleEffect = ({
  isActive,
  particleCount = 20,
  colors = ['#f1c40f', '#f39c12', '#ffffff'],
  originX = '50%',
  originY = '50%',
  size = 100,
  duration = 1500,
  onComplete,
}) => {
  const [sparkles, setSparkles] = useState([]);

  useEffect(() => {
    if (isActive) {
      const newSparkles = Array.from({ length: particleCount }, (_, i) => ({
        id: `sparkle-${Date.now()}-${i}`,
        color: colors[randomInt(0, colors.length)],
        angle: (360 / particleCount) * i + random(-10, 10),
        distance: random(size * 0.3, size),
        sparkleSize: random(3, 8),
        delay: random(0, 0.2),
      }));

      setSparkles(newSparkles);

      const timer = setTimeout(() => {
        setSparkles([]);
        onComplete?.();
      }, duration);

      return () => clearTimeout(timer);
    } else {
      setSparkles([]);
    }
  }, [isActive, particleCount, colors, size, duration, onComplete]);

  return (
    <AnimatePresence>
      {sparkles.length > 0 && (
        <div
          className="absolute pointer-events-none"
          style={{
            left: originX,
            top: originY,
            transform: 'translate(-50%, -50%)',
          }}
        >
          {sparkles.map((sparkle) => {
            const rad = (sparkle.angle * Math.PI) / 180;
            const x = Math.cos(rad) * sparkle.distance;
            const y = Math.sin(rad) * sparkle.distance;

            return (
              <motion.div
                key={sparkle.id}
                className="absolute rounded-full"
                style={{
                  width: sparkle.sparkleSize,
                  height: sparkle.sparkleSize,
                  backgroundColor: sparkle.color,
                  boxShadow: `0 0 ${sparkle.sparkleSize * 2}px ${sparkle.color}`,
                }}
                initial={{
                  x: 0,
                  y: 0,
                  opacity: 1,
                  scale: 0,
                }}
                animate={{
                  x: x,
                  y: y,
                  opacity: [1, 1, 0],
                  scale: [0, 1.5, 0],
                }}
                transition={{
                  duration: 0.8,
                  delay: sparkle.delay,
                  ease: 'easeOut',
                  opacity: { times: [0, 0.3, 1] },
                }}
              />
            );
          })}
        </div>
      )}
    </AnimatePresence>
  );
};

// Burst effect for rewards
export const BurstEffect = ({
  isActive,
  ringCount = 3,
  color = '#f39c12',
  size = 200,
  duration = 1000,
  onComplete,
}) => {
  const [rings, setRings] = useState([]);

  useEffect(() => {
    if (isActive) {
      const newRings = Array.from({ length: ringCount }, (_, i) => ({
        id: `ring-${Date.now()}-${i}`,
        delay: i * 0.15,
      }));

      setRings(newRings);

      const timer = setTimeout(() => {
        setRings([]);
        onComplete?.();
      }, duration);

      return () => clearTimeout(timer);
    } else {
      setRings([]);
    }
  }, [isActive, ringCount, duration, onComplete]);

  return (
    <AnimatePresence>
      {rings.length > 0 && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          {rings.map((ring) => (
            <motion.div
              key={ring.id}
              className="absolute rounded-full border-4"
              style={{
                borderColor: color,
                width: size,
                height: size,
              }}
              initial={{
                scale: 0,
                opacity: 0.8,
              }}
              animate={{
                scale: 3,
                opacity: 0,
              }}
              transition={{
                duration: 0.8,
                delay: ring.delay,
                ease: 'easeOut',
              }}
            />
          ))}
        </div>
      )}
    </AnimatePresence>
  );
};

// Achievement Reward Confetti - Two crossing streams from bottom corners
const DEFAULT_CONFETTI_COLORS = ['#f1c40f', '#e74c3c', '#3498db', '#2ecc71', '#9b59b6', '#f39c12', '#1abc9c'];

export const AchievementConfetti = ({
  isActive,
  particleCount = 80,
  colors = DEFAULT_CONFETTI_COLORS,
  duration = 4000,
  onComplete,
}) => {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    if (isActive) {
      const newParticles = [];
      const halfCount = Math.floor(particleCount / 2);

      // Left stream - bottom-left shooting to top-right
      for (let i = 0; i < halfCount; i++) {
        newParticles.push({
          id: `left-particle-${Date.now()}-${i}`,
          color: colors[randomInt(0, colors.length)],
          size: random(8, 16),
          startX: random(0, window.innerWidth * 0.1), // Bottom-left corner
          startY: window.innerHeight,
          xVelocity: random(400, 700), // Move right and up
          yVelocity: random(-800, -500),
          rotation: random(0, 720),
          particleDuration: random(2.5, 4),
          shape: ['square', 'circle', 'rectangle'][randomInt(0, 3)],
          delay: random(0, 0.4),
        });
      }

      // Right stream - bottom-right shooting to top-left
      for (let i = 0; i < halfCount; i++) {
        newParticles.push({
          id: `right-particle-${Date.now()}-${i}`,
          color: colors[randomInt(0, colors.length)],
          size: random(8, 16),
          startX: random(window.innerWidth * 0.9, window.innerWidth), // Bottom-right corner
          startY: window.innerHeight,
          xVelocity: random(-700, -400), // Move left and up
          yVelocity: random(-800, -500),
          rotation: random(0, 720),
          particleDuration: random(2.5, 4),
          shape: ['square', 'circle', 'rectangle'][randomInt(0, 3)],
          delay: random(0, 0.4),
        });
      }

      setParticles(newParticles);

      // Cleanup after animation
      const timer = setTimeout(() => {
        setParticles([]);
        onComplete?.();
      }, duration);

      return () => clearTimeout(timer);
    } else {
      setParticles([]);
    }
  }, [isActive, particleCount, colors, duration, onComplete]);

  const shapeStyles = {
    square: { borderRadius: '2px' },
    circle: { borderRadius: '50%' },
    rectangle: { borderRadius: '1px' },
  };

  return (
    <AnimatePresence>
      {particles.length > 0 && (
        <div className="fixed inset-0 pointer-events-none z-[9999] overflow-hidden">
          {particles.map((particle) => (
            <motion.div
              key={particle.id}
              className="absolute pointer-events-none"
              style={{
                left: particle.startX,
                top: particle.startY,
                width: particle.shape === 'rectangle' ? particle.size * 0.4 : particle.size,
                height: particle.size,
                backgroundColor: particle.color,
                ...shapeStyles[particle.shape],
                ...(particle.shape === 'rectangle' && {
                  width: particle.size * 0.4,
                  height: particle.size
                }),
              }}
              initial={{
                opacity: 1,
                x: 0,
                y: 0,
                rotate: 0,
                scale: 1,
              }}
              animate={{
                opacity: [1, 1, 1, 0],
                x: particle.xVelocity,
                y: [0, particle.yVelocity, particle.yVelocity + 800], // Up then fall due to gravity
                rotate: particle.rotation,
                scale: [1, 1.3, 1, 0.6],
              }}
              transition={{
                duration: particle.particleDuration,
                delay: particle.delay,
                ease: [0.25, 0.46, 0.45, 0.94],
                opacity: { times: [0, 0.3, 0.7, 1] },
                y: {
                  type: 'spring',
                  damping: 12,
                  stiffness: 40,
                  velocity: -600
                },
              }}
            />
          ))}
        </div>
      )}
    </AnimatePresence>
  );
};

export default ConfettiEffect;
